import React, { useState } from 'react';

// Define types for our component
interface CsvToMarkdownProps {}

// CSV to Markdown conversion function
const csvToMarkdown = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
    alignments: string[];
    minColumnWidth: number;
    prettify: boolean;
    escapeSpecialChars: boolean;
  }
): string => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return 'No data to display';
    }
    
    // Initialize markdown table
    let markdownTable = '';
    let startRow = 0;
    let columnCount = 0;
    
    // Process headers if they exist
    if (options.header && lines.length > 0) {
      const headerRow = lines[0];
      const headers = parseCSVLine(headerRow, options.delimiter);
      columnCount = headers.length;
      
      // Add header row
      markdownTable += '| ';
      for (const header of headers) {
        const headerValue = options.trimValues ? header.trim() : header;
        const escapedHeader = options.escapeSpecialChars ? escapeMarkdownChars(headerValue) : headerValue;
        
        if (options.prettify) {
          // Pad header to minimum width
          const paddedHeader = padString(escapedHeader, options.minColumnWidth);
          markdownTable += paddedHeader + ' | ';
        } else {
          markdownTable += escapedHeader + ' | ';
        }
      }
      markdownTable += '\n';
      
      // Add separator row
      markdownTable += '| ';
      for (let i = 0; i < headers.length; i++) {
        let separator;
        
        if (options.alignments[i]) {
          switch (options.alignments[i]) {
            case 'center':
              separator = ':' + '-'.repeat(options.minColumnWidth) + ':';
              break;
            case 'right':
              separator = '-'.repeat(options.minColumnWidth) + ':';
              break;
            case 'left':
              separator = ':' + '-'.repeat(options.minColumnWidth);
              break;
            default:
              separator = '-'.repeat(options.minColumnWidth + 2);
          }
        } else {
          separator = '-'.repeat(options.minColumnWidth + 2);
        }
        
        markdownTable += separator + ' | ';
      }
      markdownTable += '\n';
      startRow = 1;
    }
    
    // Process data rows
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      // Set columnCount based on first row if header was not used
      if (i === 0 && !options.header) {
        columnCount = values.length;
      }
      
      if (values.length > 0) {
        markdownTable += '| ';
        
        // Ensure all rows have the same number of columns
        for (let j = 0; j < columnCount; j++) {
          const value = j < values.length ? values[j] : '';
          const cellValue = options.trimValues ? value.trim() : value;
          const escapedValue = options.escapeSpecialChars ? escapeMarkdownChars(cellValue) : cellValue;
          
          if (options.prettify) {
            // Pad cell content to minimum width
            const paddedValue = padString(escapedValue, options.minColumnWidth);
            markdownTable += paddedValue + ' | ';
          } else {
            markdownTable += escapedValue + ' | ';
          }
        }
        
        markdownTable += '\n';
      }
    }
    
    return markdownTable;
  } catch (error) {
    throw error;
  }
};

// Helper function to escape Markdown special characters
const escapeMarkdownChars = (text: string): string => {
  return text
    .replace(/\|/g, '\\|')
    .replace(/\*/g, '\\*')
    .replace(/_/g, '\\_')
    .replace(/\[/g, '\\[')
    .replace(/\]/g, '\\]')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/\#/g, '\\#')
    .replace(/\+/g, '\\+')
    .replace(/\-/g, '\\-')
    .replace(/\`/g, '\\`');
};

// Helper function to pad a string to a minimum length
const padString = (str: string, minLength: number): string => {
  if (str.length >= minLength) {
    return str;
  }
  
  const padding = ' '.repeat(minLength - str.length);
  return str + padding;
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Main component to convert CSV to Markdown table
const CsvToMarkdown: React.FC<CsvToMarkdownProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the Markdown output
  const [markdownOutput, setMarkdownOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('table');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isMarkdownGenerated, setIsMarkdownGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for preview
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [prettify, setPrettify] = useState<boolean>(true);
  const [escapeSpecialChars, setEscapeSpecialChars] = useState<boolean>(true);
  const [minColumnWidth, setMinColumnWidth] = useState<number>(3);
  
  // Column alignment
  const [alignments, setAlignments] = useState<string[]>([]);
  const [columnCount, setColumnCount] = useState<number>(0);
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsMarkdownGenerated(false);
    setShowPreview(false);
    
    // Try to determine column count for alignment setup
    try {
      const lines = e.target.value.split(/\r?\n/);
      if (lines.length > 0) {
        const firstRow = lines[0];
        const columns = parseCSVLine(firstRow, delimiter);
        setColumnCount(columns.length);
        
        // Initialize alignments if needed
        if (alignments.length !== columns.length) {
          setAlignments(Array(columns.length).fill(''));
        }
      }
    } catch (e) {
      // Just skip if we can't parse
    }
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const csv = event.target.result as string;
        setCsvInput(csv);
        setError('');
        setIsMarkdownGenerated(false);
        setShowPreview(false);
        
        // Try to determine column count for alignment setup
        try {
          const lines = csv.split(/\r?\n/);
          if (lines.length > 0) {
            const firstRow = lines[0];
            const columns = parseCSVLine(firstRow, delimiter);
            setColumnCount(columns.length);
            
            // Initialize alignments if needed
            if (alignments.length !== columns.length) {
              setAlignments(Array(columns.length).fill(''));
            }
          }
        } catch (e) {
          // Just skip if we can't parse
        }
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Product,Category,Price,Stock,Rating
Basic T-Shirt,Clothing,$19.99,120,4.2
Premium Hoodie,Clothing,$49.99,65,4.8
Wireless Earbuds,Electronics,$89.99,42,4.5
Smart Watch,Electronics,$199.99,30,4.7
Running Shoes,Footwear,$79.99,80,4.3
Yoga Mat,Fitness,$29.99,55,4.6
Protein Powder,Nutrition,$39.99,100,4.4
Water Bottle,Accessories,$14.99,150,4.1`;

    setCsvInput(exampleCsv);
    setError('');
    setIsMarkdownGenerated(false);
    setShowPreview(false);
    setMarkdownOutput('');
    
    // Set up column alignments for example data
    setColumnCount(5);
    setAlignments(['left', 'left', 'right', 'right', 'center']);
  };

  // Convert CSV to Markdown
  const convertCsvToMarkdown = () => {
    setError('');
    setIsLoading(true);
    setIsMarkdownGenerated(false);
    setMarkdownOutput('');
    setShowPreview(false);
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the Markdown table
      const options = {
        header: hasHeader,
        delimiter,
        trimValues,
        skipEmptyLines,
        alignments,
        minColumnWidth,
        prettify,
        escapeSpecialChars
      };
      
      const markdown = csvToMarkdown(csvInput, options);
      setMarkdownOutput(markdown);
      setIsMarkdownGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to Markdown: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setMarkdownOutput('');
    setError('');
    setFileName('table');
    setIsMarkdownGenerated(false);
    setShowPreview(false);
    setAlignments([]);
    setColumnCount(0);
  };

  // Copy Markdown to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(markdownOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Markdown file
  const downloadMarkdown = () => {
    const blob = new Blob([markdownOutput], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'table'}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Update column alignment
  const handleAlignmentChange = (index: number, alignment: string) => {
    const newAlignments = [...alignments];
    newAlignments[index] = alignment;
    setAlignments(newAlignments);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to Markdown Table Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to Markdown table format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Product,Category,Price,Stock
T-Shirt,Clothing,$19.99,120
Headphones,Electronics,$89.99,42"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="prettify"
                    checked={prettify}
                    onChange={(e) => setPrettify(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="prettify" className="ml-2 block text-sm text-gray-700">
                    Prettify table (align columns)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="escapeSpecialChars"
                    checked={escapeSpecialChars}
                    onChange={(e) => setEscapeSpecialChars(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="escapeSpecialChars" className="ml-2 block text-sm text-gray-700">
                    Escape special Markdown characters
                  </label>
                </div>
                
                <div>
                  <label htmlFor="minColumnWidth" className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Column Width
                  </label>
                  <input
                    type="number"
                    id="minColumnWidth"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={minColumnWidth}
                    onChange={(e) => setMinColumnWidth(parseInt(e.target.value, 10))}
                    min={1}
                    max={50}
                  />
                </div>
                
                {columnCount > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Column Alignments
                    </label>
                    <div className="space-y-2">
                      {Array.from({ length: columnCount }).map((_, index) => (
                        <div key={index} className="flex items-center">
                          <span className="w-20 text-sm text-gray-600">Column {index + 1}:</span>
                          <select
                            className="p-1 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={alignments[index] || ''}
                            onChange={(e) => handleAlignmentChange(index, e.target.value)}
                          >
                            <option value="">Default</option>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToMarkdown}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Markdown'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Markdown Output
            </label>
            
            {isMarkdownGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Markdown'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadMarkdown}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={togglePreview}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isMarkdownGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Markdown output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isMarkdownGenerated && !showPreview && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="markdownOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={markdownOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
            
            {!error && isMarkdownGenerated && showPreview && (
              <div className="flex-grow flex flex-col border border-gray-300 rounded-md p-3 bg-white overflow-auto">
                <div className="prose prose-sm max-w-none">
                  {/* Simple markdown preview - in a real app you'd use a library like react-markdown */}
                  <div className="markdown-preview font-mono whitespace-pre overflow-auto">
                    {markdownOutput}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {isMarkdownGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to Markdown!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated Markdown table for use in your documentation, README files, or other Markdown documents.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to Markdown Table Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool parses CSV (Comma-Separated Values) data and converts it into Markdown table format. CSV files are plain text files that store tabular data with each value separated by a delimiter (typically a comma). The converter generates properly formatted Markdown tables that you can easily embed in GitHub READMEs, documentation, or any platform that supports Markdown.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>Table Headers</strong> - Option to use first row as headers</li>
              <li><strong>Prettified Tables</strong> - Format tables with consistent spacing for better readability</li>
              <li><strong>Column Alignment</strong> - Specify left, center, or right alignment for each column</li>
              <li><strong>Special Character Escaping</strong> - Automatic escaping of Markdown special characters</li>
              <li><strong>Live Preview</strong> - See how your table will look in Markdown</li>
              <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
              <li><strong>Markdown Download</strong> - Save your table as a Markdown file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Markdown Table Syntax</h3>
            <p className="text-gray-600">
              Markdown tables use a simple syntax of pipes and dashes:
            </p>
            <pre className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto mt-2">
| Header 1 | Header 2 | Header 3 |
| -------- | -------- | -------- |
| Value 1  | Value 2  | Value 3  |
| Value 4  | Value 5  | Value 6  |
            </pre>
            <p className="text-gray-600 mt-2">
              You can also specify column alignment using colons in the separator row:
            </p>
            <pre className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto mt-2">
| Left-aligned | Center-aligned | Right-aligned |
| :----------- | :-----------: | ------------: |
| Left         | Center        | Right         |
            </pre>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Creating tables for GitHub README files</li>
              <li>Generating documentation tables</li>
              <li>Converting spreadsheet data into Markdown format</li>
              <li>Creating tables for technical blog posts</li>
              <li>Adding tabular data to Markdown-based wikis</li>
              <li>Preparing data tables for static site generators</li>
              <li>Creating tables for Jupyter notebooks</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better table structure</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
              <li>Avoid special characters in headers for better compatibility</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Markdown Table Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent column spacing for better readability</li>
              <li>Choose appropriate column alignments for your data (e.g., right-align numeric columns)</li>
              <li>Keep tables reasonably sized for better readability (consider breaking large tables)</li>
              <li>Add explanatory text before or after complex tables</li>
              <li>Use descriptive headers that clearly explain the data</li>
              <li>For complex data, consider using footnotes with reference markers</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToMarkdown;